import { create } from 'zustand';
import type { EditorTarget } from '@poi-planner/shared';

// ── View Modes ──
export type ViewMode = 'kanban' | 'compact';

// ── UI State ──
interface UIState {
  // Editor state - unified pattern for all editors
  activeEditor: EditorTarget | null;

  // Inline time editor (separate from modal editors)
  inlineTimeEdit: { dayId: string; poiId: string } | null;

  // Layout
  sidebarCollapsed: boolean;
  viewMode: ViewMode;

  // Proximity sorting target (for future use)
  targetPoiId: string | null;

  // Focus mode - filter sidebar to nearby POIs
  focusModeEnabled: boolean;

  // Day focus view - single day with map
  focusedDayId: string | null;

  // Actions
  openEditor: (target: EditorTarget) => void;
  closeEditor: () => void;

  // Convenience methods for opening specific editors
  openPOIEditor: (mode: 'create' | 'edit', poiId?: string) => void;
  openAccomEditor: (mode: 'create' | 'edit', accomId?: string, targetDayId?: string) => void;
  openTransportEditor: (mode: 'create' | 'edit', transportId?: string, targetDayId?: string, targetOrder?: number) => void;

  // Legacy aliases for compatibility
  closePOIEditor: () => void;
  closeAccomEditor: () => void;
  closeTransportEditor: () => void;

  // Other actions
  setInlineTimeEdit: (edit: { dayId: string; poiId: string } | null) => void;
  toggleSidebar: () => void;
  setViewMode: (mode: ViewMode) => void;
  setTargetPoiId: (poiId: string | null) => void;
  setFocusModeEnabled: (enabled: boolean) => void;

  // Day focus view actions
  enterDayFocus: (dayId: string) => void;
  exitDayFocus: () => void;
  setFocusedDayId: (dayId: string) => void;
}

// ── Store ──
export const useUIStore = create<UIState>((set) => ({
  activeEditor: null,
  inlineTimeEdit: null,
  sidebarCollapsed: false,
  viewMode: 'kanban',
  targetPoiId: null,
  focusModeEnabled: false,
  focusedDayId: null,

  // Generic editor actions
  openEditor: (target) => set({ activeEditor: target }),
  closeEditor: () => set({ activeEditor: null }),

  // Convenience methods that construct EditorTarget
  openPOIEditor: (mode, poiId) => set({
    activeEditor: {
      type: 'poi',
      mode,
      entityId: poiId ?? null,
    },
  }),

  openAccomEditor: (mode, accomId, targetDayId) => set({
    activeEditor: {
      type: 'accommodation',
      mode,
      entityId: accomId ?? null,
      context: targetDayId ? { dayId: targetDayId } : undefined,
    },
  }),

  openTransportEditor: (mode, transportId, targetDayId, targetOrder) => set({
    activeEditor: {
      type: 'transport',
      mode,
      entityId: transportId ?? null,
      context: (targetDayId || targetOrder != null)
        ? { dayId: targetDayId, order: targetOrder }
        : undefined,
    },
  }),

  // Legacy close aliases (all just close the active editor)
  closePOIEditor: () => set({ activeEditor: null }),
  closeAccomEditor: () => set({ activeEditor: null }),
  closeTransportEditor: () => set({ activeEditor: null }),

  // Other actions
  setInlineTimeEdit: (edit) => set({ inlineTimeEdit: edit }),
  toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
  setViewMode: (mode) => set({ viewMode: mode }),
  setTargetPoiId: (poiId) => set({ targetPoiId: poiId }),
  setFocusModeEnabled: (enabled) => set({ focusModeEnabled: enabled }),

  // Day focus view actions
  enterDayFocus: (dayId) => set({ focusedDayId: dayId, targetPoiId: null }),
  exitDayFocus: () => set({ focusedDayId: null, targetPoiId: null }),
  setFocusedDayId: (dayId) => set({ focusedDayId: dayId, targetPoiId: null }),
}));
